import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, EmptyState, BotAvatar, AccountHealthChip, LiveChip } from "@/components/shared";
import { useLiveEvents } from "@/hooks/useLiveEvents";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { UserPlus, RefreshCw, Users2, Bot, ArrowRight } from "lucide-react";

export default function TelegramAccounts() {
  const [items, setItems] = useState(null);
  const navigate = useNavigate();
  const load = useCallback(
    () => api.get("/telegram-accounts").then((r) => setItems(r.data.items)).catch(() => setItems([])),
    []
  );
  useEffect(() => { load(); }, [load]);

  // The server pushes sync progress, so there is no timed guess about when the
  // background job finished.
  useLiveEvents(["account.updated", "account.synced", "account.connected", "bot.upserted"], () => load());

  const sync = async (id, e) => {
    e.stopPropagation();
    try { await api.post(`/telegram-accounts/${id}/sync`); toast.success("Sync started", { description: "Discovering bots in the background." }); }
    catch (err) { toast.error(err?.response?.data?.detail || "Sync failed"); }
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Telegram Accounts" desc="Connect a Telegram account once — every bot it owns is discovered and managed here."
        actions={<div className="flex items-center gap-2"><LiveChip /><Button size="sm" onClick={() => navigate("/accounts/add")} className="gap-1.5" data-testid="btn-add-account"><UserPlus size={15} /> Add Account</Button></div>} testId="accounts-header" />

      {!items ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-40 w-full" />)}</div> :
        items.length === 0 ? (
          <div className="bg-card border border-border rounded-lg"><EmptyState icon={Users2} title="No Telegram accounts connected"
            desc="Connect your Telegram account to automatically discover and manage every bot you own."
            action={<Button onClick={() => navigate("/accounts/add")} className="gap-1.5"><UserPlus size={15} /> Connect Telegram Account</Button>} testId="empty-accounts" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((a) => (
              <div key={a.id} data-testid={`account-card-${a.id}`} onClick={() => navigate(`/accounts/${a.id}`)}
                className="bg-card border border-border rounded-lg p-5 cursor-pointer hover:border-primary/40 transition-colors group">
                <div className="flex items-center gap-3">
                  <BotAvatar name={a.first_name} username={a.username} size={44} />
                  <div className="min-w-0 flex-1"><div className="font-semibold truncate">{[a.first_name, a.last_name].filter(Boolean).join(" ") || a.username}</div>
                    <div className="text-xs text-muted-foreground mono truncate">{a.username ? "@" + a.username : a.phone_masked}</div></div>
                </div>
                <div className="mt-3 flex items-center justify-between"><AccountHealthChip status={a.status} />{a.premium && <span className="text-[10px] uppercase text-amber-500 font-semibold">Premium</span>}</div>
                <div className="mt-4 grid grid-cols-4 gap-1 text-center">
                  {[["Owned", a.counts.owned], ["Managed", a.counts.FULL_MANAGED], ["Token", a.counts.TOKEN_CONNECTED], ["Owner", a.counts.OWNER_CONTROL]].map(([l, v]) => (
                    <div key={l}><div className="text-lg font-bold mono">{v}</div><div className="text-[10px] uppercase text-muted-foreground">{l}</div></div>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
                  <span className="text-[11px] text-muted-foreground">{a.last_synced_at ? "Synced " + new Date(a.last_synced_at).toLocaleTimeString() : "Not synced yet"}</span>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => sync(a.id, e)} data-testid={`btn-sync-account-${a.id}`}><RefreshCw size={14} /></Button>
                    <ArrowRight size={15} className="text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
